/**
 * Build a self-contained HTML document from the /api/export/data payload.
 *
 * Design goals:
 *   - Single file the user can open in any browser or upload to Claude / etc.
 *   - Text-heavy structure (h1/h2/tables) so LLMs parse it cleanly.
 *   - Inline SVG activity heatmap — no external assets, works offline.
 *   - Human date formatting, kg/reps totals, and per-exercise sub-tables.
 */
import { format, parseISO } from 'date-fns';

const escape = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const fmtDate = (iso) => {
  try { return format(parseISO(iso), 'MMM d, yyyy'); } catch { return iso || ''; }
};
const fmtDateTime = (iso) => {
  try { return format(parseISO(iso), 'MMM d, yyyy · HH:mm'); } catch { return iso || ''; }
};
const fmtDuration = (s) => {
  if (!s) return '0s';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  if (m === 0) return `${sec}s`;
  if (sec === 0) return `${m}m`;
  return `${m}m ${sec}s`;
};

/** 15-column-wide activity heatmap SVG mirroring the in-app one. */
function heatmapSvg(activity, maxDays = 365) {
  if (!activity?.length) return '<p class="muted">No activity data yet.</p>';
  const days = activity.slice(-maxDays);
  const CELL = 12, GAP = 2, COLS = 15;
  const rows = Math.ceil(days.length / COLS);
  const w = COLS * (CELL + GAP);
  const h = rows * (CELL + GAP);
  const opacity = (min) => Math.min(1, Math.max(0.18, min / 45));
  const cells = days.map((d, i) => {
    const col = i % COLS;
    const row = Math.floor(i / COLS);
    const x = col * (CELL + GAP);
    const y = row * (CELL + GAP);
    const o = d.minutes > 0 ? opacity(d.minutes).toFixed(2) : 0;
    const fill = d.minutes > 0 ? `rgba(192,57,47,${o})` : 'rgba(25,28,33,0.08)';
    return `<rect x="${x}" y="${y}" width="${CELL}" height="${CELL}" rx="2" ry="2" fill="${fill}"><title>${escape(d.date)} — ${d.minutes}min</title></rect>`;
  }).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="Activity heatmap">${cells}</svg>`;
}

function pbTable(pbs, exMap) {
  if (!pbs?.length) return '<p class="muted">No personal bests yet.</p>';
  const sorted = [...pbs].sort((a, b) => {
    const na = exMap[a.exercise_id]?.name || '';
    const nb = exMap[b.exercise_id]?.name || '';
    return na.localeCompare(nb);
  });
  const rows = sorted.map((pb) => {
    const ex = exMap[pb.exercise_id] || {};
    const cardio = pb.set_type === 'cardio';
    const metric = cardio
      ? `${((pb.distance_m || 0) / 1000).toFixed(2)}km · ${fmtDuration(pb.duration_seconds)}`
      : `${pb.weight}kg × ${pb.reps}`;
    return `<tr>
      <td>${escape(ex.name || 'Unknown')}</td>
      <td class="muted">${escape(ex.muscle_group || '—')}</td>
      <td class="mono">${escape(metric)}</td>
      <td class="muted">${fmtDate(pb.logged_at)}</td>
    </tr>`;
  }).join('');
  return `<table><thead><tr><th>Exercise</th><th>Muscle</th><th>Best</th><th>Date</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function measurementsTable(rows) {
  if (!rows?.length) return '<p class="muted">No body measurements logged.</p>';
  const asc = [...rows].sort((a, b) => new Date(a.logged_at) - new Date(b.logged_at));
  const tr = asc.map((m) => `<tr>
    <td class="mono">${fmtDate(m.logged_at)}</td>
    <td>${m.weight_kg ?? '—'}</td>
    <td>${m.chest_cm ?? '—'}</td>
    <td>${m.waist_cm ?? '—'}</td>
    <td>${m.hip_cm ?? '—'}</td>
    <td>${m.arm_cm ?? '—'}</td>
    <td>${m.thigh_cm ?? '—'}</td>
    <td>${m.body_fat_pct ?? '—'}</td>
  </tr>`).join('');
  return `<table><thead><tr>
    <th>Date</th><th>Weight (kg)</th><th>Chest (cm)</th><th>Waist (cm)</th><th>Hip (cm)</th>
    <th>Arm (cm)</th><th>Thigh (cm)</th><th>Body Fat %</th>
  </tr></thead><tbody>${tr}</tbody></table>`;
}

function workoutsSection(workouts, sets, exMap) {
  if (!workouts?.length) return '<p class="muted">No workouts logged.</p>';
  const setsByWorkout = sets.reduce((acc, s) => {
    (acc[s.workout_id] = acc[s.workout_id] || []).push(s);
    return acc;
  }, {});

  const sortedWorkouts = [...workouts].sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
  return sortedWorkouts.map((w) => {
    const ws = setsByWorkout[w.id] || [];
    const byEx = ws.reduce((acc, s) => {
      (acc[s.exercise_id] = acc[s.exercise_id] || []).push(s);
      return acc;
    }, {});
    const summary = `${ws.length} sets · ${fmtDuration(w.duration_seconds || 0)}`;
    const exBlocks = Object.entries(byEx).map(([exId, list]) => {
      const ex = exMap[exId] || {};
      const rows = list.map((s, i) => {
        const cardio = s.set_type === 'cardio';
        const metric = cardio
          ? `${((s.distance_m || 0) / 1000).toFixed(2)}km · ${fmtDuration(s.duration_seconds)}`
          : `${s.weight}kg × ${s.reps}`;
        return `<tr>
          <td class="muted">#${i + 1}</td>
          <td class="mono">${escape(metric)}</td>
          <td class="muted">${s.rpe ? `RPE ${s.rpe}` : ''}</td>
          <td class="muted">${s.is_best ? 'PB' : ''}</td>
        </tr>`;
      }).join('');
      return `<div class="ex-block">
        <p class="ex-heading"><strong>${escape(ex.name || 'Exercise')}</strong> <span class="muted">${escape(ex.muscle_group || '')}${ex.equipment_type ? ` · ${escape(ex.equipment_type)}` : ''}</span></p>
        <table class="compact"><tbody>${rows}</tbody></table>
      </div>`;
    }).join('');
    return `<article class="workout">
      <h3>${escape(w.name || 'Workout')}</h3>
      <p class="muted mono">${fmtDateTime(w.started_at)} · ${escape(summary)}</p>
      ${exBlocks || '<p class="muted">No sets logged for this workout.</p>'}
    </article>`;
  }).join('');
}

export function buildExportHtml(data) {
  const exMap = (data.exercises || []).reduce((acc, e) => ((acc[e.id] = e), acc), {});
  const profile = data.profile || {};
  const totals = data.totals || {};
  const exportedAt = fmtDateTime(data.exported_at);

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>BRETH Export — ${escape(profile.display_name || 'Athlete')}</title>
<style>
  :root { --red: #C0392F; --ink: #191C21; --bone: #EDEAE3; --muted: #6b6f78; --hair: #DCD7CB; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: var(--ink); background: var(--bone); }
  main { max-width: 900px; margin: 0 auto; padding: 32px 24px 96px; }
  header { border-bottom: 2px solid var(--ink); padding-bottom: 20px; margin-bottom: 32px; }
  h1 { margin: 0 0 4px; font-size: 42px; letter-spacing: -0.02em; font-weight: 900; text-transform: uppercase; }
  h2 { margin: 40px 0 12px; font-size: 22px; text-transform: uppercase; letter-spacing: 0.06em; border-top: 1px solid var(--hair); padding-top: 20px; }
  h3 { margin: 24px 0 4px; font-size: 18px; }
  p { line-height: 1.55; }
  p.muted, .muted { color: var(--muted); font-size: 13px; }
  .mono { font-variant-numeric: tabular-nums; font-family: "SF Mono", Menlo, Consolas, monospace; }
  .kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 20px 0 8px; }
  .kpi { background: #fff; border: 1px solid var(--hair); border-radius: 12px; padding: 14px 16px; }
  .kpi small { display:block; text-transform: uppercase; font-size: 10px; letter-spacing: 0.18em; color: var(--muted); }
  .kpi .val { font-size: 28px; font-weight: 900; font-variant-numeric: tabular-nums; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0 16px; font-size: 14px; }
  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid var(--hair); }
  th { font-weight: 800; font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted); }
  table.compact th, table.compact td { padding: 4px 8px; font-size: 13px; }
  article.workout { background: #fff; border: 1px solid var(--hair); border-radius: 12px; padding: 18px 20px; margin-bottom: 14px; page-break-inside: avoid; }
  .ex-block { margin-top: 10px; padding-top: 10px; border-top: 1px dashed var(--hair); }
  .ex-heading { margin: 0 0 4px; }
  footer { margin-top: 60px; padding-top: 20px; border-top: 1px solid var(--hair); color: var(--muted); font-size: 12px; }
  a { color: var(--red); }
  @media print { body { background: #fff; } main { padding: 16px; } article.workout { border: none; padding: 0 0 12px; } }
</style>
</head>
<body>
<main>
  <header>
    <p class="muted">BRETH · Full Data Export</p>
    <h1>${escape(profile.display_name || 'Athlete')}</h1>
    <p class="muted mono">Exported ${escape(exportedAt)}</p>
  </header>

  <section class="kpi-row">
    <div class="kpi"><small>Workouts</small><div class="val">${totals.workouts ?? 0}</div></div>
    <div class="kpi"><small>Sets</small><div class="val">${totals.sets ?? 0}</div></div>
    <div class="kpi"><small>Reps</small><div class="val">${(totals.reps ?? 0).toLocaleString()}</div></div>
    <div class="kpi"><small>Volume</small><div class="val">${Math.round(totals.volume_kg ?? 0).toLocaleString()}<small style="display:inline;font-size:12px;color:var(--muted);letter-spacing:0;margin-left:4px;text-transform:none;">kg</small></div></div>
  </section>
  ${totals.cardio_distance_m > 0 ? `
  <section class="kpi-row" style="grid-template-columns: repeat(2, 1fr);">
    <div class="kpi"><small>Cardio Distance</small><div class="val">${(totals.cardio_distance_m / 1000).toFixed(1)}<small style="display:inline;font-size:12px;color:var(--muted);letter-spacing:0;margin-left:4px;text-transform:none;">km</small></div></div>
    <div class="kpi"><small>Cardio Time</small><div class="val">${fmtDuration(totals.cardio_duration_s)}</div></div>
  </section>` : ''}

  <h2>Activity Heatmap</h2>
  <p class="muted">Daily training minutes. Denser + darker cells = longer sessions.</p>
  ${heatmapSvg(data.activity_heatmap)}

  <h2>Personal Bests</h2>
  ${pbTable(data.personal_bests, exMap)}

  <h2>Body Measurements</h2>
  ${measurementsTable(data.measurements)}

  <h2>Goals</h2>
  ${(data.goals || []).length === 0 ? '<p class="muted">No goals set.</p>' :
    `<table><thead><tr><th>Exercise</th><th>Metric</th><th>Target</th><th>Deadline</th><th>Status</th></tr></thead><tbody>${
      (data.goals || []).map((g) => `<tr>
        <td>${escape(exMap[g.exercise_id]?.name || '—')}</td>
        <td>${escape(g.metric || '')}</td>
        <td class="mono">${g.target_value ?? ''}</td>
        <td class="muted">${g.deadline ? fmtDate(g.deadline) : '—'}</td>
        <td class="muted">${escape(g.status || 'active')}</td>
      </tr>`).join('')
    }</tbody></table>`}

  <h2>Full Workout History</h2>
  ${workoutsSection(data.workouts, data.sets, exMap)}

  <footer>
    <p>Schema version ${escape(data.schema_version)} · Generated by BRETH · <a href="https://brethapp.com">brethapp.com</a></p>
    <p class="mono">This is a self-contained HTML report. Save the file, upload it, print it or open it in any browser.</p>
  </footer>
</main>
</body>
</html>`;
}

/** Triggers a browser download for the generated HTML. */
export function downloadHtmlExport(data) {
  const html = buildExportHtml(data);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const dateStr = new Date().toISOString().slice(0, 10);
  const nameSlug = (data.profile?.display_name || 'athlete').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  a.href = url;
  a.download = `breth-${nameSlug}-${dateStr}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
